import { FC, memo, useMemo } from "react"
import classNames from "classnames"
import Tooltip from "../shared/Tooltip"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import { Icon, IconColor } from "@/components/shared/Icon"
import VitalIcons, { VitalConfig } from "@/components/shared/VitalIcons"
import { PageRoutes } from "@/constants/routes"
import { useFastAlarmClearanceEnabled } from "@/hooks/useFastAlarmClearanceEnabled"
import { useTruncation } from "@/hooks/useTruncation"
import { useUserRole } from "@/hooks/useUserRole"
import { AlertVariant, AlertVitals, TypefaceVariant } from "@/types/alert"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { ButtonVariant, TypefaceColors } from "@/types/index"
import { getCellIconName, getCellIconTestId } from "@/utils/cells"
import { renderWithTooltip } from "@/utils/index"
import "./index.scss"

const VITAL_CONFIGS: readonly VitalConfig[] = [
   {
      highKey: "highHeartRate",
      lowKey: "lowHeartRate",
      vitalName: "heartRate",
      vitalKey: "heartRate",
   },
   {
      highKey: "highRespiratoryRate",
      lowKey: "lowRespiratoryRate",
      vitalName: "respiratoryRate",
      vitalKey: "respiratoryRate",
   },
] as const

export interface AlertCellProps {
   cellId: string
   isCustom?: boolean
   hasDoorSensor?: boolean
   doorSensorChargeLevel?: number | null
   doorSensorChargeThreshold?: number
   systemConditionType?: AlertSystemConditionType
   alertVariant?: keyof typeof AlertVariant
   typefaceColor?: keyof typeof TypefaceVariant
   vitals?: AlertVitals
   onClick?: () => void
   onClearAlert?: () => void
   onViewDetails?: () => void
}

const AlertCell: FC<AlertCellProps> = ({
   cellId,
   isCustom = false,
   hasDoorSensor,
   doorSensorChargeLevel,
   doorSensorChargeThreshold = 20,
   systemConditionType,
   alertVariant,
   typefaceColor,
   vitals,
   onClick,
   onClearAlert = () => {},
   onViewDetails = () => {},
}) => {
   const [cellIdRef, isCellIdTruncated] = useTruncation<HTMLSpanElement>(cellId)
   const userCanAccessDetailView = useUserRole(PageRoutes.CELL_DETAIL_OVERVIEW)
   const fastAlarmClearanceEnabled = useFastAlarmClearanceEnabled()

   const doorSensorLowBattery = useMemo(
      () =>
         !!hasDoorSensor &&
         !!doorSensorChargeLevel &&
         doorSensorChargeLevel <= doorSensorChargeThreshold,
      [hasDoorSensor, doorSensorChargeLevel, doorSensorChargeThreshold]
   )

   const alertVariantClassName = alertVariant
      ? `alert-cell--${AlertVariant[alertVariant]}`
      : ""

   const typefaceClassName = typefaceColor
      ? `alert-cell__typeface--${TypefaceVariant[typefaceColor]}`
      : ""

   const typefaceIconColor =
      typefaceColor === TypefaceColors.GUNMETAL
         ? IconColor.GUNMETAL
         : IconColor.WHITE

   const iconName = getCellIconName(
      isCustom,
      hasDoorSensor,
      doorSensorLowBattery
   )

   const cellIcon = (
      <Icon
         className={classNames({
            "alert-cell__door-sensor-battery-icon": doorSensorLowBattery,
         })}
         size="16"
         color={typefaceIconColor}
         name={iconName}
         data-testid={getCellIconTestId(iconName)}
      />
   )

   return (
      <div
         className={classNames(
            "alert-cell",
            alertVariantClassName,
            typefaceClassName,
            {
               "alert-cell--clickable": onClick,
               "alert-cell--custom": isCustom,
            }
         )}
         onClick={onClick}
      >
         <div className="alert-cell__header">
            <span className="alert-cell__cell-id-wrapper">
               {doorSensorLowBattery ? (
                  <Tooltip content="Low battery in door sensor">
                     {cellIcon}
                  </Tooltip>
               ) : (
                  cellIcon
               )}
               {renderWithTooltip(
                  cellIdRef,
                  "alert-cell__cell-id",
                  cellId,
                  isCellIdTruncated
               )}
            </span>

            <div className="alert-cell__buttons">
               {fastAlarmClearanceEnabled && (
                  <Button
                     className="alert-cell__clear-button"
                     iconName="clearAlerts"
                     variant={ButtonVariant.SMALL}
                     iconSize="12"
                     iconPosition={ButtonIconPosition.LEFT}
                     onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onClearAlert()
                     }}
                  >
                     Clear
                  </Button>
               )}
               {userCanAccessDetailView && (
                  <Tooltip content="Go to detail overview">
                     <Button
                        className="alert-cell__view-details-button"
                        variant={ButtonVariant.SMALL}
                        iconName="visual"
                        iconSize="12"
                        iconPosition={ButtonIconPosition.LEFT}
                        onClick={(e) => {
                           e.stopPropagation()
                           e.preventDefault()
                           onViewDetails()
                        }}
                     />
                  </Tooltip>
               )}
            </div>
         </div>

         <div className="alert-cell__footer">
            {systemConditionType === AlertSystemConditionType.NO_VITALS ? (
               <span className="alert-cell__status">
                  <Icon
                     name="noVitalSign"
                     size="24"
                     color={typefaceIconColor}
                  />
                  <span>
                     No Vitals
                     {!hasDoorSensor && (
                        <>
                           {" /"}
                           <span>Empty Cell</span>
                        </>
                     )}
                  </span>
               </span>
            ) : (
               <VitalIcons
                  vitals={vitals}
                  typefaceIconColor={typefaceIconColor}
                  vitalConfigs={VITAL_CONFIGS}
               />
            )}
         </div>
      </div>
   )
}

export default memo(AlertCell, (prevProps, nextProps) => {
   const keys: (keyof AlertCellProps)[] = [
      "cellId",
      "isCustom",
      "hasDoorSensor",
      "systemConditionType",
      "doorSensorChargeLevel",
      "alertVariant",
      "typefaceColor",
   ]

   const primitivesEqual = keys.every(
      (key) => prevProps[key] === nextProps[key]
   )

   const vitalsEqual =
      JSON.stringify(prevProps.vitals) === JSON.stringify(nextProps.vitals)

   return primitivesEqual && vitalsEqual
})
